import type { ThemeColors } from './colors';
import type { ChecklistBrand } from '../types';

/**
 * Overrides `primary`/`primarySoft` with an insurer's accent for the
 * insurer-link journey — a strip plus an accent, not a full re-theme (see
 * project decision in the brand-morphing feature notes). Every other token
 * is passed through untouched, in particular the map marker, opportunity,
 * risk, progress and news families: those carry fixed meaning (CLAUDE.md
 * hard rule 3) and must never take on an insurer's color.
 *
 * `useMarkerLayer.web.ts` keys a Leaflet icon-rebuild effect on `colors`
 * object identity, so two calls for the same theme + accent must return the
 * SAME object — otherwise every render would rebuild every marker icon.
 * Cached in a module Map for that reason.
 */
const cache = new Map<string, ThemeColors>();

// Matches the alpha each theme already uses for `primarySoft` in colors.ts
// (light: rgba(46, 107, 80, 0.08), dark: rgba(105, 178, 140, 0.12)) — the
// branded soft fill should whisper exactly as loud as the stock one does.
const PRIMARY_SOFT_ALPHA: Record<'light' | 'dark', number> = {
  light: 0.08,
  dark: 0.12,
};

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return [r, g, b];
}

export function brandedPalette(
  base: ThemeColors,
  theme: 'light' | 'dark',
  brand: Pick<ChecklistBrand, 'accentLight' | 'accentDark'>
): ThemeColors {
  const accent = theme === 'dark' ? brand.accentDark : brand.accentLight;
  const cacheKey = `${theme}|${accent}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const [r, g, b] = hexToRgb(accent);
  const next: ThemeColors = {
    ...base,
    primary: accent,
    primarySoft: `rgba(${r}, ${g}, ${b}, ${PRIMARY_SOFT_ALPHA[theme]})`,
  };
  cache.set(cacheKey, next);
  return next;
}
